import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IReceipt } from '../models/receipts';
import { IItem } from '../models/item';
import { IInvoice } from '../models/invoice';
import { ILocation } from '../models/location.model';
import { LocationStore } from '../stores/location.store';

export interface IDemoCheckItemInput {
  name: string;
  price: number;
  quantity?: number;
}

// =============================================================================
// DEMO-ONLY  —  fabricated check + payment flow for sales/demo links.
// =============================================================================
// Builds a client-only, fabricated check from a JSON-encoded `items` query param
// on the scan URL, e.g.:
//   https://<domain>/:beaconId?items=[{"name":"Cheeseburger","price":12.99,"quantity":2}]
// It lets someone pull up a REAL location's theme and walk a realistic-looking
// bill + payment flow WITHOUT a real POS ticket or payment processor behind it.
// NOTHING here touches the backend: the check, invoice, and "successful" payment
// are all fabricated in the browser.
//
// This service is the single switch for the whole demo feature: `isActive` is
// true for the rest of the session once a demo check is built, and every demo
// branch elsewhere keys off it. To find every demo touchpoint, grep the codebase
// for "DEMO-ONLY".
//
// -----------------------------------------------------------------------------
// If/when we make this a REAL feature (server-provided demo tables, or a genuine
// "pre-load a check" capability), here is everything that is currently faked and
// would need to be revisited. Each site is tagged "DEMO-ONLY":
//   1. scan.page.ts .................. parses `?items=`, injects the fake check.
//   2. table-scan.guard.ts ........... preserves query params through the redirect
//                                      (this one is a general fix, safe to keep).
//   3. table.service.ts .............. skips the real table re-fetch while active,
//                                      so heartbeat/table-refresh can't overwrite
//                                      the fake check.
//   4. order-pay.page.ts ............. forces the check view on entry.
//   5. invoice.service.ts ............ fakes requestInvoice (no price check),
//                                      fakes submitPaymentForInvoice (no charge),
//                                      and no-ops abandon/renew (no backend lease).
//   6. router.service.ts ............. skips Adyen's eager getPaymentMethods fetch
//                                      on the way to the bill (it would fail on the
//                                      fake invoice and leave a loader stuck up).
//   7. v2-payment-options component .. renders the demo pay button instead of the
//                                      real provider payment UI.
//   8. v2-payment-options-demo-footer  the demo pay button; simulates the charge.
// A real implementation would instead go through the normal invoice/payment APIs
// against a real (or seeded) ticket, and most of the above branches would be
// deleted rather than reimplemented.
// -----------------------------------------------------------------------------
@Injectable({
  providedIn: 'root'
})
export class DemoCheckService {
  private static readonly demoCheckId = 'demo-check';

  constructor(private locationStore: LocationStore) {}

  private readonly _isActive$ = new BehaviorSubject(false);

  // True once a demo check has been built for the current session. Other parts of
  // the app (TableService.openTable) check this to avoid clobbering the fabricated
  // check with a real (likely empty) re-fetch from the backend.
  get isActive(): boolean {
    return this._isActive$.getValue();
  }

  set isActive(v: boolean) {
    this._isActive$.next(v);
  }

  // Returns null if there's no `items` param, it isn't valid JSON, or it doesn't
  // contain any usable items.
  buildDemoCheck(rawItemsParam: string | null, currency: string): IReceipt | null {
    const items = this.parseItems(rawItemsParam);
    if (!items || items.length === 0) {
      return null;
    }

    const checkItems: IItem[] = items.map((item, index) =>
      this.toCheckItem(item, index)
    );
    const subtotal = checkItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    this.isActive = true;

    return {
      childTickets: [],
      created: new Date().toISOString(),
      currency,
      discounts: [],
      employeeName: '',
      isClaimed: false,
      items: checkItems,
      name: 'Demo Check',
      otherPayments: [],
      paidItems: [],
      payments: [],
      externalId: DemoCheckService.demoCheckId,
      posTicketNumber: '-1',
      invoices: [],
      totals: {
        discounts: 0,
        due: subtotal,
        otherPayments: 0,
        otherCharges: 0,
        serviceCharge: 0,
        subtotal,
        tax: 0,
        total: subtotal,
        splitDenominator: 1,
        splitNumerator: 1
      },
      isPaid: false,
      seat: 1,
      _id: DemoCheckService.demoCheckId
    };
  }

  // Builds a fabricated invoice that mirrors the demo check so the Your Bill screen
  // and payment confirmation can render without hitting the real invoice/payment
  // APIs (which would fail a price check against the non-existent POS ticket).
  // Pass `paid: true` (with the tip in cents) to produce the post-payment invoice
  // shown on the confirmation screen.
  buildDemoInvoice(
    check: IReceipt,
    opts: { tipCents?: number; paid?: boolean } = {}
  ): IInvoice {
    const tipCents = opts.tipCents ?? 0;
    const paid = opts.paid ?? false;
    const total = check?.totals?.total ?? 0;
    const now = new Date().toISOString();

    return {
      _id: DemoCheckService.demoCheckId,
      id: DemoCheckService.demoCheckId,
      amountAuthorized: paid ? total + tipCents : 0,
      amountPaid: paid ? total + tipCents : 0,
      archived: false,
      childTickets: [],
      claimedItems: [],
      collected: paid,
      created: now,
      customer: '',
      customerName: '',
      employeeName: '',
      isTab: false,
      items: check?.items ?? [],
      location: this.locationStore.currentLocation ?? ({} as ILocation),
      paid,
      paidItems: paid ? check?.items ?? [] : [],
      payments: [],
      preauths: [],
      table: '',
      tableName: check?.name ?? '',
      ticket: check?.externalId ?? DemoCheckService.demoCheckId,
      totals: check?.totals,
      transactionStartDate: now,
      posTicketNumber: check?.posTicketNumber ?? '-1',
      paidDate: new Date(),
      tipAmountPaid: paid ? tipCents : 0,
      tipAmount: tipCents,
      currency: check?.currency
    };
  }

  private parseItems(rawItemsParam: string | null): IDemoCheckItemInput[] | null {
    if (!rawItemsParam) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawItemsParam);
      if (!Array.isArray(parsed)) {
        return null;
      }

      return parsed.filter(
        (item): item is IDemoCheckItemInput =>
          !!item &&
          typeof item.name === 'string' &&
          typeof item.price === 'number'
      );
    } catch (e) {
      console.log('Failed to parse demo check items from query string', e);
      return null;
    }
  }

  private toCheckItem(item: IDemoCheckItemInput, index: number): IItem {
    const quantity =
      typeof item.quantity === 'number' && item.quantity > 0
        ? item.quantity
        : 1;

    return {
      comment: '',
      modifiers: [],
      discounts: [],
      posMenuItemId: `demo-${index}`,
      menuId: 'demo',
      name: item.name,
      posId: `${index}`,
      externalId: `demo-${index}`,
      price: Math.round(item.price * 100),
      quantity,
      _id: `demo-${index}`,
      uiIdentifier: ''
    };
  }
}
